import React, { useState } from 'react';
import { Col } from '../../bootstrap/index.jsx'
import FillPhaseEdit from './FillPhaseEdit.jsx';
import CommentPhaseEdit from './CommentPhaseEdit.jsx'
import ApprovalPhaseEdit from './ApprovalPhaseEdit.jsx';
import DocusignPhaseEdit from './DocusignPhaseEdit.jsx'
import { useEffect } from 'react';

import { useDrag } from 'react-dnd'
import { MuiIcon } from "../../common/Icons.jsx";

import { monitoring_report_project_reports_path } from '~/routes';
import { PHASE_TYPE_STYLES } from './phaseTypes.js';

export const PHASE_MAPPING = {
  'fill': {...PHASE_TYPE_STYLES.fill, component: FillPhaseEdit},
  'commenting': {...PHASE_TYPE_STYLES.commenting, component: CommentPhaseEdit},
  'approval': {...PHASE_TYPE_STYLES.approval, component: ApprovalPhaseEdit},
  'docusign': {...PHASE_TYPE_STYLES.docusign, component: DocusignPhaseEdit}
}

export const NODE_SIZE = 8


// Node positions for the phase graph
export const phaseElemPosition = (x,y,size, scale=1) => {
  return {
    x: x*size*scale + size*2/3 + (x * size * 2.75), // Circle diameter + initial padding + spacing
    y: y*size + size + (y * size / 1.35) // Circle diameter + initial padding + spacing
  }
}

const isInsidePhaseElem = (pos, phaseX, phaseY, size) => {
  const phasePos = phaseElemPosition(phaseX, phaseY, size);
  return (
    phasePos.x + size/2 >= pos.x && 
    phasePos.x - size/2 <= pos.x &&
    phasePos.y + size/2 >= pos.y && 
    phasePos.y - size/2 <= pos.y 
  )
}

export const DraggablePhaseElem = (props) => {
  const { type } = props.phase;

  const [{ isDragging }, drag, preview] = useDrag({
    item: { id: props.phase.id, type: type },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  })

  return <PhaseElem {...props} dragRef={drag} isDragging={isDragging}/>
}

// Single node in the graph
export const PhaseElem = ({ phase, selected, x, y, size, onClick, onMouseEnter, onMouseLeave, circleProps = {}, iconProps = { style: {}}, nameProps = {}, scale = 1, dragRef = null, isDragging = false, isWidget = false, isKey=false, showDaysPassed = true}) => {
  const {id, monitoring_report_id, type, body, longest_delay, reports_in_current_phase, reports_rejected, reports_approved, reports_in_review_phase_pending_revert, review_avg_pass_days } = phase;

  const pos = phaseElemPosition(x, y, size);

  const isSurpassQueueTime = body && body.longest_delay_limit && longest_delay && body.longest_delay_limit < longest_delay

  // Status numbers under each node when displayed as widget
  let phaseStatistic = ''
  let phaseStatisticBold = ''
  let statusLeft = ''
  let statusRight = ''
  switch(type) {
    case 'fill':
      phaseStatistic = reports_in_current_phase;
      if(!isKey){
        phaseStatistic = reports_rejected;
        phaseStatisticBold = reports_in_current_phase
      }else{
        phaseStatistic = I18n.t('widget_key_rejected')
        phaseStatisticBold = I18n.t('widget_key_in_draft')
      }
      statusLeft = 'draft'
      statusRight = 'rejected'
      break;
    case 'approval':
      if(!isKey){
        phaseStatistic = reports_approved;
        phaseStatisticBold = reports_in_current_phase
      }else{
        phaseStatistic = I18n.t('widget_key_approved')
        phaseStatisticBold = I18n.t('widget_key_pending_approval')
      }
      statusLeft = 'assigned'
      statusRight = 'approved'
      break;
    case 'commenting':
      if(!isKey){
        phaseStatistic = reports_in_review_phase_pending_revert
        phaseStatisticBold = reports_in_current_phase
      }else{
        phaseStatistic = I18n.t('widget_key_blocked')
        phaseStatisticBold = I18n.t('widget_key_in_review')
      }
      statusLeft = 'assigned'
      statusRight = 'rejected'
      break;
    case 'docusign':
        if(!isKey){
          phaseStatistic = reports_in_review_phase_pending_revert
          phaseStatisticBold = reports_in_current_phase
        }else{
          phaseStatistic = I18n.t('widget_key_blocked')
          phaseStatisticBold = I18n.t('widget_key_in_review')
        }
        statusLeft = 'assigned'
        statusRight = 'rejected'
        break;
    default:
      // code block
  }

  const updateTextBg = () => {
    const textEles = document.querySelectorAll('.phase-data');
    textEles.forEach((textEle) => {
      const textBbx = textEle.getBBox();
      const bgEle = textEle.previousElementSibling;
      if(bgEle){
        bgEle.setAttribute('x', textBbx.x-2);
        bgEle.setAttribute('y', textBbx.y-0.25);
        bgEle.setAttribute('width', textBbx.width+4);
        bgEle.setAttribute('height', textBbx.height+0.5);
        bgEle.setAttribute("fill", '#DDD');
        textEle.parentNode.insertBefore(bgEle, textEle);
      }
    })
  }

  useEffect(() => {
    updateTextBg();
  })

  const widgetNewTabProps = isWidget && !isKey ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  const phaseNodeHref = (!isWidget || isKey || !monitoring_report_id || !id)
    ? '#'
    : monitoring_report_project_reports_path({ id: monitoring_report_id, phase_id: id, _options: true });

  return <g
      onClick={isWidget ? ()=>{} : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className='workflow-widget-node noselect'
      style={{cursor: 'pointer'}}
      ref={dragRef}
    >
    <a href={phaseNodeHref} {...widgetNewTabProps}>
      <circle 
        stroke='#DDD' 
        strokeWidth={1} 
        style={{opacity: (selected || isWidget) ? '1.0' : (!isDragging ? '0.3' : '0.05')}}
        className={'full-opacity-hover'}
        fill={PHASE_MAPPING[type].color} 
        cx={pos.x} 
        cy={pos.y} 
        r={size/2 * scale}
        {...circleProps} 
      />
      <circle
        cx={pos.x} 
        cy={pos.y} 
        r={size * scale}
        fill="#FFF" 
        opacity={0}
        stroke="none"
      />
      <text 
        x={pos.x - NODE_SIZE*scale/2 + 2} // Adjust position (+2)
        y={pos.y + NODE_SIZE*scale/2 - 2} // Adjust position (-2)
        fill='#FFF'
        {...iconProps} 
        style={{fontFamily: 'adalicons', fontSize: NODE_SIZE*scale - 4, ...iconProps.style}} // Slightly smaller fontsize than the node
      >
        {PHASE_MAPPING[type].icon}
      </text>
    </a>
    <rect className='phase-data-bg'></rect>
    {/* Data below phase */}
    {isWidget && <text
      className='phase-data'
      style={{fontSize: NODE_SIZE*scale/4, textShadow: isKey ? '#DDD -2px -2px 0px, #DDD -2px 2px 0px, #DDD 2px -2px 0px, #DDD 2px 2px 0px' : ''}}
      x={pos.x - NODE_SIZE*scale/2 + 4} // Adjust position (+2)
      y={pos.y + NODE_SIZE*scale - 3.5} // Adjust position (-2)
      fill='#666'
      textAnchor="middle"
      onClick = {(e) => e.stopPropagation()}
    >
      <a href={isKey ? '#' : monitoring_report_project_reports_path({ id: monitoring_report_id, phase_id: id, status: statusLeft, _options: true })} {...widgetNewTabProps}><tspan>{phaseStatisticBold + " |"}</tspan></a>
      <a href={isKey ? '#' : monitoring_report_project_reports_path({ id: monitoring_report_id, phase_id: id, status: statusRight, _options: true})} {...widgetNewTabProps}> {" " + phaseStatistic} </a>
    </text>}
    {/* Review phase top left warning icon */}
    {isWidget && type == 'commenting' && isSurpassQueueTime &&
      <MuiIcon type="new_releases" height={NODE_SIZE*scale/4} width={NODE_SIZE*scale/4} x={pos.x - NODE_SIZE*scale/2} y={pos.y - NODE_SIZE*scale/2} style={{ fill: 'red' }} />
    }
    {/* Review phase right info */}
    {isWidget && type == 'commenting' && !isKey && showDaysPassed == 'true' &&
      <>
        <MuiIcon type="timelapse" height={NODE_SIZE*scale/4} width={NODE_SIZE*scale/4} x={pos.x + NODE_SIZE*scale*0.7} y={pos.y - NODE_SIZE*scale/4 - 0.2} className='icon-grey'/>
        <text style={{fontSize: NODE_SIZE*scale/4}} x={pos.x + NODE_SIZE*scale} y={pos.y - 0.5}>{review_avg_pass_days || 0}</text>
      </>
    }
    <text
      style={{fontSize: NODE_SIZE/4, pointerEvents: 'none', textShadow: 'rgb(255, 255, 255) -2px -2px 0px, rgb(255, 255, 255) -2px 2px 0px, rgb(255, 255, 255) 2px -2px 0px, rgb(255, 255, 255) 2px 2px 0px'}}
      x={pos.x - NODE_SIZE/2 + 4} // Adjust position (+2)
      y={pos.y + NODE_SIZE - 0.5} // Adjust position (-2)
      fill='#666'
      textAnchor="middle"
      {...nameProps}
    >
      {body ? body.name : ''}
    </text>
  </g>
}

const range = (start, count) => {
  return Array.apply(0, Array(count))
    .map((element, index) => index + start);
}


const getPositionsBetween = (pos1, pos2) => {
  let positions = [];
  range(Math.min(pos1.x, pos2.x), Math.abs(pos1.x-pos2.x) + 1).forEach(x => {
    range(Math.min(pos1.y, pos2.y), Math.abs(pos1.y-pos2.y) + 1).forEach(y => {
      positions.push([x,y]);
    })
  });
  return positions;
}

// Connector between 2 nodes
export const PhaseConnectorElem = ({ pos1, pos2, pos1Level, pos2Level, strokeColor='#DDD', onMouseEnter, onMouseLeave, nodePositions, pathProps, arrowHeadProps, isWidget=false }) => {

  let handle1 = `${(pos1.x + pos2.x) / 2},${pos1.y}`
  let handle2 = `${(pos1.x + pos2.x) / 2},${pos2.y}`

  if (pos1Level && pos2Level){
    const connectionLength = Math.abs(pos1Level.x - pos2Level.x);
    // Adjust the curvature if the connection is long 
    if (connectionLength > 1) {
      // Adjust handles by getting the emtpy space
      const emptySpace = getPositionsBetween(pos1Level, pos2Level).filter(p => nodePositions.findIndex(np => np[0] == p[0] && np[1] == p[1]) == -1);
      const empty2Count = emptySpace.filter(e => e[1] !== pos1Level.y).length;
      const empty1Count = emptySpace.filter(e => e[1] !== pos2Level.y).length;
      if (empty1Count > empty2Count){
        // Increase handle x-pos to make it curve close to latter node
        handle1 = `${(pos1.x + pos2.x) / 2 + NODE_SIZE*connectionLength},${pos1.y}` 
        handle2 = `${(pos1.x + pos2.x) / 2 + NODE_SIZE*2},${pos2.y}` 
      } else if(empty1Count < empty2Count){
        // Decrease the handle x-pos to make it curve closer to former node
        handle1 = `${(pos1.x + pos2.x) / 2 - NODE_SIZE*2},${pos1.y}` 
        handle2 = `${(pos1.x + pos2.x) / 2 - NODE_SIZE*connectionLength},${pos2.y}` 
      } 
    }
  }

  // Phase connector as straight lines when displayed in widget
  // const straightLineD = pos1.y < pos2.y ? `M ${pos1.x + 1 + NODE_SIZE/2} ${pos1.y} V ${pos2.y} H ${pos2.x}` 
  // : `M ${pos1.x + 1 + NODE_SIZE/2} ${pos1.y} H ${pos2.x} V ${pos2.y}`

  // Draw connector lines which with a small cap before the node (-1/-2)
  return <g>
    <path d={`M ${pos1.x + 1 + NODE_SIZE/2} ${pos1.y}
      C 
        ${handle1} 
        ${handle2}
        ${pos2.x - NODE_SIZE/2 - 2},${pos2.y}
      `}
      fill="none"
      stroke={strokeColor}
      strokeWidth="0.5"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...pathProps}
    />
    <polygon fill={strokeColor} points={`
      ${pos2.x - NODE_SIZE/2-NODE_SIZE/10 - 1},${pos2.y-NODE_SIZE/10}
      ${pos2.x - NODE_SIZE/2 - 1},${pos2.y}
      ${pos2.x - NODE_SIZE/2-NODE_SIZE/10 - 1},${pos2.y+NODE_SIZE/10}
    `}
      {...arrowHeadProps}
    />
  </g>
}


export const PhaseConnectorCreateElem = ({fromPhase,  svg, phases, onConnect, onEnd, svgScale}) => {
  const [targetPos, setTargetPos] = useState({x: 0, y: 0, target: null});
  const startPos = phaseElemPosition(fromPhase.x_level, fromPhase.y_level, NODE_SIZE)

  const resolveTarget = (mousePos) => {
    const targetIdx = phases.findIndex(p => isInsidePhaseElem(mousePos, p.x_level, p.y_level, NODE_SIZE));
    if (targetIdx != -1) {
      return {...phaseElemPosition(phases[targetIdx].x_level, phases[targetIdx].y_level, NODE_SIZE), target: phases[targetIdx]};
    }
    return {...mousePos, target: null};
  }

  const handleMouseMove = (e) => {
    let bb = svg.current.getBoundingClientRect();
    setTargetPos(resolveTarget({x: (e.clientX - bb.left) / svgScale, y: (e.clientY - bb.top) / svgScale}))
  }

  const handleClick = () => {
    if (targetPos.target){
      onConnect(fromPhase, targetPos.target)
    } else {
      onEnd();
    }
  }

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, true);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove, true);
    }
  },[])

  useEffect(() => {
    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('click', handleClick, true);
    }
  }, [targetPos])





  return <PhaseConnectorElem pos1={startPos} pos2={targetPos} strokeColor='#666' />
}

